import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  ArrowDownToLine, 
  ArrowUpFromLine, 
  ArrowLeftRight, 
  SlidersHorizontal,
  ClipboardCheck,
  Plus,
  History
} from 'lucide-react';

const movementTypes = [
  { id: 'GR', name: 'Goods Receipt', icon: ArrowDownToLine, color: 'text-[#34C759]', bg: 'bg-[#34C759]/10' },
  { id: 'GI', name: 'Goods Issue', icon: ArrowUpFromLine, color: 'text-[#FF3B30]', bg: 'bg-[#FF3B30]/10' },
  { id: 'TR', name: 'Transfer', icon: ArrowLeftRight, color: 'text-[#007AFF]', bg: 'bg-[#007AFF]/10' },
  { id: 'ADJ', name: 'Adjustment', icon: SlidersHorizontal, color: 'text-[#FF9500]', bg: 'bg-[#FF9500]/10' },
  { id: 'CC', name: 'Cycle Count', icon: ClipboardCheck, color: 'text-[#AF52DE]', bg: 'bg-[#AF52DE]/10' }
];

export default function TransactionsPage() {
  const [activeTab, setActiveTab] = useState('movements');
  const [movements, setMovements] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [transactionType, setTransactionType] = useState(null);
  
  const [formData, setFormData] = useState({
    warehouse_id: '',
    to_warehouse_id: '',
    item_id: '',
    quantity: 0,
    unit_cost: 0,
    reference: '',
    reason: '',
    notes: '',
    counted_quantity: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [movementsRes, warehousesRes, itemsRes] = await Promise.all([
        api.get('/movements?limit=100'),
        api.get('/warehouses'),
        api.get('/items')
      ]);
      setMovements(movementsRes.data);
      setWarehouses(warehousesRes.data);
      setItems(itemsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (type) => {
    setTransactionType(type);
    setFormData({
      warehouse_id: '',
      to_warehouse_id: '',
      item_id: '',
      quantity: 0,
      unit_cost: 0,
      reference: '',
      reason: '',
      notes: '',
      counted_quantity: 0
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      let endpoint = '';
      let payload = {};

      switch (transactionType) {
        case 'GR':
          endpoint = '/movements/goods-receipt';
          payload = {
            warehouse_id: formData.warehouse_id,
            item_id: formData.item_id,
            quantity: formData.quantity,
            unit_cost: formData.unit_cost,
            reference: formData.reference,
            notes: formData.notes
          };
          break;
        case 'GI':
          endpoint = '/movements/goods-issue';
          payload = {
            warehouse_id: formData.warehouse_id,
            item_id: formData.item_id,
            quantity: formData.quantity,
            reference: formData.reference,
            notes: formData.notes
          };
          break;
        case 'TR':
          endpoint = '/movements/transfer';
          payload = {
            from_warehouse_id: formData.warehouse_id,
            to_warehouse_id: formData.to_warehouse_id,
            item_id: formData.item_id,
            quantity: formData.quantity,
            reference: formData.reference,
            notes: formData.notes
          };
          break;
        case 'ADJ':
          endpoint = '/movements/adjustment';
          payload = {
            warehouse_id: formData.warehouse_id,
            item_id: formData.item_id,
            quantity: formData.quantity,
            reason: formData.reason,
            reference: formData.reference,
            notes: formData.notes
          };
          break;
        case 'CC':
          endpoint = '/movements/cycle-count';
          payload = {
            warehouse_id: formData.warehouse_id,
            item_id: formData.item_id,
            counted_quantity: formData.counted_quantity,
            notes: formData.notes
          };
          break;
        default:
          return;
      }

      await api.post(endpoint, payload);
      toast.success('Transaction posted successfully');
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Transaction failed');
    }
  };

  const getMovementBadge = (type) => {
    const mt = movementTypes.find(m => m.id === type);
    if (!mt) return null;
    return (
      <Badge className={`${mt.bg} ${mt.color}`}>
        {mt.id}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString();
  };

  return (
    <div className="space-y-6" data-testid="transactions-page">
      {/* Header */}
      <div>
        <h1 className="text-heading-black text-3xl lg:text-4xl text-[#0A0A0A] tracking-tight">TRANSACTIONS</h1>
        <p className="text-[#0A0A0A]/60 mt-1">Process stock movements and view history</p>
      </div>

      {/* Transaction Type Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {movementTypes.map((type) => (
          <Card 
            key={type.id}
            className="border border-[#E5E5EA] shadow-none hover:border-[#007AFF] transition-colors cursor-pointer"
            onClick={() => openDialog(type.id)}
            data-testid={`transaction-card-${type.id.toLowerCase()}`}
          >
            <CardContent className="p-6 text-center">
              <div className={`w-12 h-12 ${type.bg} rounded-sm flex items-center justify-center mx-auto mb-3`}>
                <type.icon className={`w-6 h-6 ${type.color}`} />
              </div>
              <h3 className="text-heading text-sm">{type.name}</h3>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Movement History */}
      <Card className="border border-[#E5E5EA] shadow-none">
        <CardHeader className="border-b border-[#E5E5EA] pb-4">
          <CardTitle className="text-heading text-lg flex items-center gap-2">
            <History className="w-5 h-5 text-[#007AFF]" />
            Movement History
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#007AFF]"></div>
            </div>
          ) : movements.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <History className="w-12 h-12 text-[#0A0A0A]/20 mb-4" />
              <p className="text-[#0A0A0A]/60">No movements recorded</p>
              <p className="text-sm text-[#0A0A0A]/40 mt-1">Process a transaction to see history</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-b border-[#E5E5EA]">
                  <TableHead className="text-overline text-[#0A0A0A]/50">Type</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50">SKU</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50">Item</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50">Warehouse</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50 text-right">Qty</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50 text-right">Before</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50 text-right">After</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50">Reference</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50">User</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50">Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {movements.map((movement) => (
                  <TableRow key={movement.id} className="border-b border-[#E5E5EA]" data-testid={`movement-row-${movement.id}`}>
                    <TableCell>{getMovementBadge(movement.movement_type)}</TableCell>
                    <TableCell className="font-mono text-sm">{movement.item_sku}</TableCell>
                    <TableCell className="font-medium">{movement.item_name}</TableCell>
                    <TableCell className="text-[#0A0A0A]/60">{movement.warehouse_name}</TableCell>
                    <TableCell className={`text-right font-mono ${movement.quantity > 0 ? 'text-[#34C759]' : 'text-[#FF3B30]'}`}>
                      {movement.quantity > 0 ? '+' : ''}{movement.quantity}
                    </TableCell>
                    <TableCell className="text-right font-mono text-[#0A0A0A]/60">{movement.before_qty}</TableCell>
                    <TableCell className="text-right font-mono">{movement.after_qty}</TableCell>
                    <TableCell className="font-mono text-sm text-[#0A0A0A]/60">{movement.reference || '-'}</TableCell>
                    <TableCell className="text-[#0A0A0A]/60">{movement.created_by_name}</TableCell>
                    <TableCell className="text-[#0A0A0A]/60 text-sm">{formatDate(movement.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Transaction Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-heading text-xl flex items-center gap-2">
              {transactionType && (
                <>
                  {(() => {
                    const mt = movementTypes.find(m => m.id === transactionType);
                    if (!mt) return null;
                    const Icon = mt.icon;
                    return <Icon className={`w-5 h-5 ${mt.color}`} />;
                  })()}
                  {movementTypes.find(m => m.id === transactionType)?.name}
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Warehouse Selection */}
            <div className="space-y-2">
              <Label>{transactionType === 'TR' ? 'From Warehouse *' : 'Warehouse *'}</Label>
              <Select 
                value={formData.warehouse_id} 
                onValueChange={(v) => setFormData({ ...formData, warehouse_id: v })}
              >
                <SelectTrigger data-testid="transaction-warehouse-select">
                  <SelectValue placeholder="Select warehouse" />
                </SelectTrigger>
                <SelectContent>
                  {warehouses.map((wh) => (
                    <SelectItem key={wh.id} value={wh.id}>{wh.name} ({wh.code})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* To Warehouse for Transfer */}
            {transactionType === 'TR' && (
              <div className="space-y-2">
                <Label>To Warehouse *</Label>
                <Select 
                  value={formData.to_warehouse_id} 
                  onValueChange={(v) => setFormData({ ...formData, to_warehouse_id: v })}
                >
                  <SelectTrigger data-testid="transaction-to-warehouse-select">
                    <SelectValue placeholder="Select destination warehouse" />
                  </SelectTrigger>
                  <SelectContent>
                    {warehouses.filter(wh => wh.id !== formData.warehouse_id).map((wh) => (
                      <SelectItem key={wh.id} value={wh.id}>{wh.name} ({wh.code})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Item Selection */}
            <div className="space-y-2">
              <Label>Item *</Label>
              <Select 
                value={formData.item_id} 
                onValueChange={(v) => setFormData({ ...formData, item_id: v })}
              >
                <SelectTrigger data-testid="transaction-item-select">
                  <SelectValue placeholder="Select item" />
                </SelectTrigger>
                <SelectContent>
                  {items.map((item) => (
                    <SelectItem key={item.id} value={item.id}>{item.sku} - {item.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Quantity */}
            {transactionType !== 'CC' && (
              <div className="space-y-2">
                <Label>Quantity *</Label>
                <Input
                  type="number"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: parseFloat(e.target.value) || 0 })}
                  placeholder={transactionType === 'ADJ' ? "Use negative for decrease" : "Enter quantity"}
                  step="0.01"
                  required
                  data-testid="transaction-quantity-input"
                />
                {transactionType === 'ADJ' && (
                  <p className="text-xs text-[#0A0A0A]/50">Use positive number to add, negative to remove</p>
                )}
              </div>
            )}

            {/* Counted Quantity for Cycle Count */}
            {transactionType === 'CC' && (
              <div className="space-y-2">
                <Label>Counted Quantity *</Label>
                <Input
                  type="number"
                  value={formData.counted_quantity}
                  onChange={(e) => setFormData({ ...formData, counted_quantity: parseFloat(e.target.value) || 0 })}
                  placeholder="Enter physical count"
                  min="0"
                  step="0.01"
                  required
                  data-testid="transaction-counted-qty-input"
                />
              </div>
            )}

            {/* Unit Cost for Goods Receipt */}
            {transactionType === 'GR' && (
              <div className="space-y-2">
                <Label>Unit Cost *</Label>
                <Input
                  type="number"
                  value={formData.unit_cost}
                  onChange={(e) => setFormData({ ...formData, unit_cost: parseFloat(e.target.value) || 0 })}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  required
                  data-testid="transaction-unit-cost-input"
                />
              </div>
            )}

            {/* Reason for Adjustment */}
            {transactionType === 'ADJ' && (
              <div className="space-y-2">
                <Label>Reason *</Label>
                <Select 
                  value={formData.reason} 
                  onValueChange={(v) => setFormData({ ...formData, reason: v })}
                >
                  <SelectTrigger data-testid="transaction-reason-select">
                    <SelectValue placeholder="Select reason" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Damaged">Damaged</SelectItem>
                    <SelectItem value="Expired">Expired</SelectItem>
                    <SelectItem value="Lost">Lost</SelectItem>
                    <SelectItem value="Found">Found</SelectItem>
                    <SelectItem value="Correction">Correction</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Reference */}
            {transactionType !== 'CC' && (
              <div className="space-y-2">
                <Label>Reference</Label>
                <Input
                  value={formData.reference}
                  onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                  placeholder="PO number, invoice, etc."
                  data-testid="transaction-reference-input"
                />
              </div>
            )}

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes..."
                rows={2}
                data-testid="transaction-notes-input"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-[#007AFF] hover:bg-[#0066D6]" data-testid="post-transaction-btn">
                Post Transaction
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
